/**
 * MerchantPreferencias — the store's switches (roadmap v1.5, `DECLARED-FACTS-RULE`).
 *
 * There is no onboarding form: "Conectar y leer" means what it says. The one thing a
 * merchant declares today is POSTURE — a preference that selects designer-set constants
 * (how sure before a test commits, cards per week) and never touches a read. It defaults
 * to Equilibrado and lives here; `/merchant/empezar` still resolves to this page.
 *
 * Writes `declared_facts.postura` and mirrors it into `default_risk_level`, which slots and
 * tests inherit. Impersonal voice; voseo.
 */
import { useEffect, useState } from 'react'
import { Card, Text, Button, Tag } from '@nimbus-ds/components'
import { useSetMerchantChrome } from '@/components/layout/MerchantChrome'
import { authService } from '@/api/auth'
import { storeService, type Postura, type DeclaredFactsRead, type DeliveryPrefsRead } from '@/api/store'
import { trackAppEvent } from '@/lib/appTelemetry'

const WEEKDAYS: Array<{ v: number; label: string }> = [
  { v: 1, label: 'Lunes' }, { v: 2, label: 'Martes' }, { v: 3, label: 'Miércoles' }, { v: 4, label: 'Jueves' },
  { v: 5, label: 'Viernes' }, { v: 6, label: 'Sábado' }, { v: 7, label: 'Domingo' },
]
const HOURS = Array.from({ length: 24 }, (_, h) => h)
const selectCls = 'rounded-md border border-[var(--nimbus-colors-neutral-surfaceHighlight)] bg-transparent px-2 py-1.5 text-sm text-[var(--nimbus-colors-neutral-textHigh)]'

/**
 * The weekly brief's delivery switches (R2 push channel v1): by mail or not, which day and
 * hour in the store's timezone, and who receives it. Unsubscribes made from the mail footer
 * are listed here and can be undone here, nowhere else.
 */
function ResumenSemanalCard({ storeId }: { storeId: string }) {
  const [current, setCurrent] = useState<DeliveryPrefsRead | null>(null)
  const [channel, setChannel] = useState<'email' | 'none'>('email')
  const [weekday, setWeekday] = useState(1)
  const [hour, setHour] = useState(9)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  useEffect(() => {
    storeService.getDeliveryPrefs(storeId)
      .then((r) => { setCurrent(r); setChannel(r.channel); setWeekday(r.weekday); setHour(r.hour) })
      .catch(() => {})
  }, [storeId])

  if (!current) return null
  const dirty = channel !== current.channel || weekday !== current.weekday || hour !== current.hour

  async function save(extra: { resubscribe?: string[] } = {}) {
    setBusy(true); setError(null)
    try {
      const r = await storeService.putDeliveryPrefs(storeId, { channel, weekday, hour, ...extra })
      setCurrent(r); setChannel(r.channel); setWeekday(r.weekday); setHour(r.hour); setSavedAt(Date.now())
      trackAppEvent('delivery_prefs_changed', { channel: r.channel, weekday: r.weekday, hour: r.hour, resubscribed: (extra.resubscribe || []).length })
    } catch {
      setError('No se pudo guardar. Probá de nuevo en un rato.')
    } finally {
      setBusy(false)
    }
  }

  const tzLabel = current.tz.split('/').pop()?.replace(/_/g, ' ') || current.tz
  const recipients = [...current.recipients.to, ...current.recipients.cc]

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <Text fontWeight="medium">El resumen semanal</Text>
        <Text fontSize="caption" color="neutral-textLow">
          Una vez por semana llega por mail cómo viene la tienda, qué cambió y qué hay para hacer. Si no hay nada nuevo, no se manda.
        </Text>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setChannel('email')} className={`rounded-lg border px-3 py-1.5 text-sm ${channel === 'email' ? 'border-[var(--nimbus-colors-primary-interactive)] bg-[var(--nimbus-colors-primary-surface)]' : 'border-[var(--nimbus-colors-neutral-surfaceHighlight)]'}`}>Por mail</button>
          <button type="button" onClick={() => setChannel('none')} className={`rounded-lg border px-3 py-1.5 text-sm ${channel === 'none' ? 'border-[var(--nimbus-colors-primary-interactive)] bg-[var(--nimbus-colors-primary-surface)]' : 'border-[var(--nimbus-colors-neutral-surfaceHighlight)]'}`}>No mandar</button>
        </div>
        {channel === 'email' && (
          <div className="flex flex-wrap items-center gap-2">
            <Text fontSize="caption">Los</Text>
            <select className={selectCls} value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} aria-label="Día">
              {WEEKDAYS.map((d) => <option key={d.v} value={d.v}>{d.label.toLowerCase()}</option>)}
            </select>
            <Text fontSize="caption">a las</Text>
            <select className={selectCls} value={hour} onChange={(e) => setHour(Number(e.target.value))} aria-label="Hora">
              {HOURS.map((h) => <option key={h} value={h}>{`${h}:00`}</option>)}
            </select>
            <Text fontSize="caption" color="neutral-textLow">hora de {tzLabel}</Text>
          </div>
        )}
        {recipients.length > 0 && (
          <Text fontSize="caption" color="neutral-textLow">
            Llega a {current.recipients.to.join(', ')}{current.recipients.cc.length > 0 ? ` · con copia a ${current.recipients.cc.join(', ')}` : ''}.
          </Text>
        )}
        {recipients.length === 0 && channel === 'email' && (
          <Text fontSize="caption" color="warning-textLow">No hay ninguna dirección a la que mandarlo.</Text>
        )}
        {current.recipients.unsubscribed.map((email) => (
          <div key={email} className="flex flex-wrap items-center gap-2">
            <Tag appearance="neutral">Dado de baja</Tag>
            <Text fontSize="caption">{email}</Text>
            <Button appearance="transparent" onClick={() => save({ resubscribe: [email] })} disabled={busy}>Volver a activar</Button>
          </div>
        ))}
        {current.last_sent_at && (
          <Text fontSize="caption" color="neutral-textLow">Último enviado el {new Date(current.last_sent_at).toLocaleDateString('es-AR')}.</Text>
        )}
        {error && <Text fontSize="caption" color="danger-textLow">{error}</Text>}
        <div className="flex items-center gap-3 pt-1">
          <Button appearance="primary" onClick={() => save()} disabled={!dirty || busy}>Guardar</Button>
          {savedAt && !dirty && <Text fontSize="caption" color="neutral-textLow">Guardado.</Text>}
        </div>
      </div>
    </Card>
  )
}

const POSTURAS: Array<{ v: Postura; title: string; body: string; note: string; recommended?: boolean }> = [
  {
    v: 'seguro',
    title: 'Seguro',
    body: 'Un resultado se marca solo cuando es sólido. Se espera más antes de decidir.',
    note: 'Para cambios grandes o difíciles de revertir.',
  },
  {
    v: 'base',
    title: 'Equilibrado',
    body: 'Un resultado se marca cuando es bastante claro. Un balance entre rapidez y confianza.',
    note: 'El punto de partida para la mayoría de las tiendas.',
    recommended: true,
  },
  {
    v: 'agil',
    title: 'Ágil',
    body: 'Se va con lo que va adelante apenas se despega. Si más tarde termina empatando, casi no se pierde nada.',
    note: 'Para tiendas con poco tráfico, o cambios rápidos y fáciles de revertir.',
  },
]

export function MerchantPreferencias() {
  const storeId = authService.getCurrentStoreId()
  const [current, setCurrent] = useState<DeclaredFactsRead | null>(null)
  const [postura, setPostura] = useState<Postura>('base')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  useSetMerchantChrome({
    title: 'Preferencias',
    subtitle: 'Cómo se decide, y cuándo llega el resumen semanal.',
    backTo: '/merchant',
    showStoreOnDesktop: true,
  })

  useEffect(() => {
    if (!storeId) return
    storeService.getDeclaredFacts(storeId)
      .then((r) => { setCurrent(r); setPostura(r.postura) })
      .catch(() => {})
    trackAppEvent('preferencias_viewed')
  }, [storeId])

  const dirty = !!current && postura !== current.postura

  async function save() {
    if (!storeId || !dirty) return
    setBusy(true); setError(null)
    try {
      const r = await storeService.putDeclaredFacts(storeId, { postura })
      setCurrent(r); setPostura(r.postura); setSavedAt(Date.now())
      trackAppEvent('postura_changed', { postura, from: current?.postura, source_before: current?.postura_source })
    } catch {
      setError('No se pudo guardar. Probá de nuevo en un rato.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      <Card>
        <div className="flex flex-col gap-3">
          <Text fontWeight="medium">¿Qué tan seguro querés estar antes de decidir?</Text>
          <Text fontSize="caption" color="neutral-textLow">
            Más seguridad, más se espera. Menos seguridad, se decide antes. Esto cambia cómo se deciden las pruebas y cuántas tarjetas llegan por semana. No cambia ningún número de tu lectura.
          </Text>
          <div className="flex flex-col gap-2">
            {POSTURAS.map((p) => {
              const on = postura === p.v
              return (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setPostura(p.v)}
                  className={`flex flex-col gap-1 rounded-lg border p-3 text-left ${on ? 'border-[var(--nimbus-colors-primary-interactive)] bg-[var(--nimbus-colors-primary-surface)]' : 'border-[var(--nimbus-colors-neutral-surfaceHighlight)] bg-transparent'}`}
                >
                  <div className="flex items-center gap-2">
                    <Text fontWeight="medium">{p.title}</Text>
                    {p.recommended && <Tag appearance="primary">Por defecto</Tag>}
                    {on && current?.postura === p.v && current.postura_source !== 'default' && <Tag appearance="neutral">Actual</Tag>}
                  </div>
                  <Text fontSize="caption">{p.body}</Text>
                  <Text fontSize="caption" color="neutral-textLow">{p.note}</Text>
                </button>
              )
            })}
          </div>
          {error && <Text fontSize="caption" color="danger-textLow">{error}</Text>}
          <div className="flex items-center gap-3 pt-1">
            <Button appearance="primary" onClick={save} disabled={!dirty || busy}>Guardar</Button>
            {savedAt && !dirty && <Text fontSize="caption" color="neutral-textLow">Guardado.</Text>}
          </div>
        </div>
      </Card>
      {storeId && <ResumenSemanalCard storeId={storeId} />}
    </div>
  )
}
